import { format, formatDistanceToNow, parseISO } from 'date-fns';

/**
 * Format a number with comma separators.
 * @example formatNumber(1234567) → "1,234,567"
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

/**
 * Format a number as compact (1K, 1.2M, etc.).
 * @example formatCompact(1234567) → "1.2M"
 */
export function formatCompact(value: number): string {
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

/**
 * Format a percentage.
 * @example formatPercent(0.1234) → "12.3%"
 */
export function formatPercent(value: number, decimals = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Format a date string as human-readable.
 * @example formatDate('2024-01-15') → "Jan 15, 2024"
 */
export function formatDate(dateStr: string, pattern = 'MMM d, yyyy'): string {
  try {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
    return format(date, pattern);
  } catch {
    return dateStr;
  }
}

/**
 * Format a date as relative time.
 * @example formatRelative('2024-01-15T10:00:00Z') → "2 hours ago"
 */
export function formatRelativeTime(dateStr: string): string {
  try {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return dateStr;
  }
}

/**
 * Truncate a string to a maximum length with ellipsis.
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 1) + '\u2026';
}

/**
 * Convert a camelCase or snake_case string to Title Case.
 * @example toTitleCase('event_name') → "Event Name"
 */
export function toTitleCase(str: string): string {
  return str
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
